import { KeyFrame } from '@stylehaven/components';

interface Thumbnail extends KeyFrame {
    TimeInSeconds: number;
}

interface ThumbnailSettings {
    width: number;
    height: number;
}

export class ThumbnailsPlugin {
    private readonly player: any;
    private readonly progressControl: any;
    private duration: number;
    private readonly offset: number;
    private readonly fps: number = 25;

    private readonly thumbnailSettings: ThumbnailSettings = { width: 135, height: 100 };
    private thumbnailContainer: HTMLElement;
    private thumbnailImg: HTMLImageElement;
    private thumbnailTimeCode: HTMLElement;

    private readonly thumbTrack: any;

    constructor(player: any, keyFrames: KeyFrame[], offset: number) {
        this.player = player;
        this.progressControl = this.player.controlBar.progressControl;
        this.duration = this.player.duration();
        this.offset = offset;

        const thumbnails: Thumbnail[] = keyFrames.map(keyFrame => {
            return {
                TimeInSeconds: this.timecodeToSeconds(keyFrame.AbsoluteTimeCode),
                ...keyFrame
            };
        });

        this.thumbTrack = thumbnails;
        this.thumbTrack.mode = 'hidden'; // Chrome needs this
    }

    public initPlugin(): void {
        this.initElements();
        this.initEvents();
    }

    private initElements(): void {
        this.thumbnailContainer = document.createElement('div');
        this.thumbnailContainer.classList.add('vjs-thumbnail-holder');

        this.thumbnailImg = document.createElement('img');
        this.thumbnailImg.classList.add('vjs-thumbnail');

        this.thumbnailTimeCode = document.createElement('div');
        this.thumbnailTimeCode.classList.add('vjs-time');

        this.thumbnailContainer.appendChild(this.thumbnailImg);
        this.thumbnailContainer.appendChild(this.thumbnailTimeCode);

        this.progressControl.el().appendChild(this.thumbnailContainer);
    }

    private initEvents(): void {
        // when the container is MP4
        this.player.on('durationchange', () => {
            this.duration = this.player.duration();
        });

        // when the container is HLS
        this.player.on('loadedmetadata', () => {
            this.duration = this.player.duration();
        });

        this.progressControl.on('mousemove', this.onHover.bind(this));
        this.progressControl.on('touchmove', this.onHover.bind(this));

        // For Android
        if (navigator.userAgent.toLowerCase().includes('android')) {
            this.progressControl.on('touchstart', this.addFakeActive.bind(this));
            this.progressControl.on('touchend', this.removeFakeActive.bind(this));
            this.progressControl.on('touchcancel', this.removeFakeActive.bind(this));
        }
    }

    private onHover(event: MouseEvent | TouchEvent): void {
        const [left, right] = this.calculateThumbnailContainerPosition(event);

        // mouseTime is the position of the mouse along the progress control bar
        // `left` applies to the mouse position relative to the player so we need
        // to remove the progress control's left offset to know the mouse position
        // relative to the progress control
        const mouseTime: number = (left / this.progressControl.width()) * this.duration;

        this.setThumbnailContainerPosition(left, right);
        this.setCurrentThumbnailImg(mouseTime);
        this.setCurrentTimeCode(mouseTime);
    }

    private calculateThumbnailContainerPosition(event: MouseEvent | TouchEvent): [number, number] {
        const pageXOffset: number = this.getScrollOffset().x;
        const clientRect: any = this.offsetParent(this.progressControl.el()).getBoundingClientRect();
        const right: number = (clientRect.width || clientRect.right) + pageXOffset;
        let pageX = 0;

        if (this.isTouchEvent(event)) {
            pageX = event.changedTouches[0].pageX;
        } else {
            pageX = event.pageX;
        }

        let left: number = pageX;

        // subtract the page offset of the positioned offset parent
        left -= this.offsetParent(this.progressControl.el()).getBoundingClientRect().left + pageXOffset;

        return [left, right];
    }

    private setThumbnailContainerPosition(left: number, right: number): void {
        const halfWidth: number = this.thumbnailSettings.width / 2;

        // make sure that the thumbnail doesn't fall off the right side of the left side of the player
        if (left < halfWidth) {
            left = 0;
        } else if (left + halfWidth > right) {
            left = right - this.thumbnailSettings.width;
        } else {
            left = left - halfWidth;
        }

        this.thumbnailContainer.style.left = left + 'px';
    }

    private setCurrentThumbnailImg(mouseTime: number): void {
        const totalThumbnails: number = this.thumbTrack.length;
        let index = 0;
        let currentThumbnailUrl: string = this.thumbnailImg.src;

        while (index < totalThumbnails) {
            const thumbnail: Thumbnail = this.thumbTrack[index];

            // if first cue is not at start, set the first cue
            if (mouseTime < this.thumbTrack[0].TimeInSeconds) {
                currentThumbnailUrl = this.thumbTrack[0].ThumbnailImagePath;
                break;
            }
            if (mouseTime > this.thumbTrack[this.thumbTrack.length - 1].TimeInSeconds) {
                currentThumbnailUrl = this.thumbTrack[this.thumbTrack.length - 1].ThumbnailImagePath;
                break;
            }
            if (thumbnail.TimeInSeconds <= mouseTime && this.thumbTrack[index + 1].TimeInSeconds > mouseTime) {
                currentThumbnailUrl = thumbnail.ThumbnailImagePath;
                break;
            }

            index++;
        }

        //Changed image?
        if (this.thumbnailImg.src !== currentThumbnailUrl) {
            this.thumbnailImg.src = currentThumbnailUrl;
        }
    }

    private setCurrentTimeCode(mouseTime: number): void {
        mouseTime += this.offset;
        mouseTime = mouseTime > 0 ? mouseTime : 0;

        let frame: number = Math.round((mouseTime * this.fps) % this.fps);

        if (frame === this.fps) {
            frame = 0;
            mouseTime++;
        }
        let timestring: string = new Date(1000 * mouseTime).toISOString().substr(11, 8);

        if (frame >= 10) {
            timestring += ':' + frame.toString();
        } else {
            timestring += ':0' + frame.toString();
        }

        this.thumbnailTimeCode.innerText = timestring;
    }

    private offsetParent(el: any): any {
        if (el.nodeName !== 'HTML' && this.getComputedStyle(el, 'position') === 'static') {
            return this.offsetParent(el.offsetParent);
        }
        return el;
    }

    private getComputedStyle(el: any, property: string): string {
        let value: string;
        const defaultView = el.ownerDocument.defaultView;
        // W3C standard way:
        if (defaultView?.getComputedStyle) {
            // sanitize property name to css notation (hypen separated words eg. font-Size)
            property = property.replace(/([A-Z])/g, '-$1').toLowerCase();
            return defaultView.getComputedStyle(el, null).getPropertyValue(property);
        } else if (el['currentStyle']) {
            // IE
            // sanitize property name to camelCase
            property = property.replace(/\-(\w)/g, (str, letter) => {
                return letter.toUpperCase();
            });
            value = el['currentStyle'][property];
            return value;
        }

        return '';
    }

    private getScrollOffset(): { x: number; y: number } {
        if (window.pageXOffset) {
            return {
                x: window.pageXOffset,
                y: window.pageYOffset
            };
        }
        return {
            x: document.documentElement.scrollLeft,
            y: document.documentElement.scrollTop
        };
    }

    private addFakeActive(): void {
        this.progressControl.addClass('fake-active');
    }

    private removeFakeActive(): void {
        this.progressControl.removeClass('fake-active');
    }

    private timecodeToSeconds(timecode: string): number {
        const timeValues: string[] = timecode.split(/[.:]+/);
        let mult = 3600;
        let seconds = 0;
        let index = 0;

        while (index < timeValues.length - 1) {
            seconds += mult * Number.parseInt(timeValues[index], 10);
            mult /= 60;
            index++;
        }

        seconds += Number.parseInt(timeValues[index], 10) / 1000;

        return seconds;
    }

    private isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
        // The TouchEvent class is not defined on Firefox, so we need to check for its existence first
        return window.TouchEvent && event instanceof TouchEvent;
    }
}

export default ThumbnailsPlugin;
